import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Animates a number from 0 up to `target` over `durationMs`, using
 * requestAnimationFrame and an ease-out cubic. Used on the results overlay
 * to make the score "count up" instead of just appearing.
 *
 * Bails to 0 for non-finite or negative targets (display will show 0 instead
 * of "NaN" or counting downward). Resets to 0 synchronously when target
 * changes so a Retry doesn't briefly show the previous run's number.
 *
 * Returns [value, skip] so callers can offer a "click to skip" UX that
 * jumps straight to the final value.
 */
export function useCountUp(target: number, durationMs = 900): [number, () => void] {
  const safeTarget = Number.isFinite(target) && target > 0 ? Math.round(target) : 0;
  const [value, setValue] = useState(0);
  const cancelRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setValue(0);
    if (safeTarget === 0) {
      cancelRef.current = null;
      return;
    }
    let raf: number;
    let cancelled = false;
    const start = performance.now();
    const tick = () => {
      if (cancelled) return;
      const elapsed = performance.now() - start;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(safeTarget * eased));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    cancelRef.current = () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      setValue(safeTarget);
    };
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [safeTarget, durationMs]);

  const skip = useCallback(() => {
    cancelRef.current?.();
  }, []);

  return [value, skip];
}
